import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, MapPin, Plus, Star } from "lucide-react";

import {
  createResidentAddress,
  deleteResidentAddress,
  updateResidentAddress,
  type Resident,
  type ResidentAddress,
  type ResidentAddressKind,
} from "@/api/residents";
import {
  RESIDENT_ADDRESS_KIND_LABELS,
  residentAddressLine,
  residentSourceChannelLabel,
} from "@/components/trust_suite/residentDisplay";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

type ResidentAddressesProps = {
  municipalityId: number;
  resident: Resident;
  onUpdated: () => void;
};

const ADDRESS_KINDS = Object.keys(RESIDENT_ADDRESS_KIND_LABELS) as ResidentAddressKind[];

export function ResidentAddresses({ municipalityId, resident, onUpdated }: ResidentAddressesProps) {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [line1, setLine1] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [kind, setKind] = useState<ResidentAddressKind>("home");
  const [label, setLabel] = useState("");

  const invalidate = () => {
    queryClient.invalidateQueries({
      queryKey: ["residents", municipalityId, "detail", resident.id],
    });
    onUpdated();
  };

  const createMutation = useMutation({
    mutationFn: () =>
      createResidentAddress(municipalityId, resident.id, {
        kind,
        label: label.trim() || null,
        address_line1: line1.trim(),
        city: city.trim() || null,
        state: state.trim() || null,
        postal_code: postalCode.trim() || null,
        is_primary: resident.addresses.length === 0,
      }),
    onSuccess: () => {
      setShowForm(false);
      setLine1("");
      setCity("");
      setState("");
      setPostalCode("");
      setLabel("");
      invalidate();
    },
  });

  const setPrimaryMutation = useMutation({
    mutationFn: (address: ResidentAddress) =>
      updateResidentAddress(municipalityId, resident.id, address.id, { is_primary: true }),
    onSuccess: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: (addressId: number) =>
      deleteResidentAddress(municipalityId, resident.id, addressId),
    onSuccess: invalidate,
  });

  return (
    <div className="space-y-3" data-testid="resident-addresses">
      {resident.addresses.length === 0 ? (
        <p className="text-sm text-muted-foreground">No addresses on file.</p>
      ) : (
        resident.addresses.map((address) => (
          <div
            key={address.id}
            className={cn(
              "rounded-lg border px-3 py-3",
              address.is_primary && "border-primary/40 bg-primary/5",
            )}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <MapPin className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="text-sm font-medium">
                    {address.label || RESIDENT_ADDRESS_KIND_LABELS[address.kind]}
                  </span>
                  {address.is_primary ? (
                    <Badge variant="secondary" className="gap-1 text-[10px]">
                      <Star className="h-3 w-3" />
                      Primary
                    </Badge>
                  ) : null}
                </div>
                <p className="mt-1 text-sm text-muted-foreground">{residentAddressLine(address)}</p>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  {residentSourceChannelLabel(address.source_channel)}
                </p>
              </div>
              <div className="flex shrink-0 flex-col gap-1">
                {!address.is_primary ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={setPrimaryMutation.isPending}
                    onClick={() => setPrimaryMutation.mutate(address)}
                  >
                    Set primary
                  </Button>
                ) : null}
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="text-destructive"
                  disabled={deleteMutation.isPending}
                  onClick={() => deleteMutation.mutate(address.id)}
                >
                  Remove
                </Button>
              </div>
            </div>
          </div>
        ))
      )}

      {showForm ? (
        <div className="space-y-3 rounded-lg border bg-muted/20 p-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="resident-address-kind">Kind</Label>
              <Select value={kind} onValueChange={(value) => setKind(value as ResidentAddressKind)}>
                <SelectTrigger id="resident-address-kind">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ADDRESS_KINDS.map((option) => (
                    <SelectItem key={option} value={option}>
                      {RESIDENT_ADDRESS_KIND_LABELS[option]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="resident-address-label">Label (optional)</Label>
              <Input
                id="resident-address-label"
                value={label}
                onChange={(event) => setLabel(event.target.value)}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="resident-address-line1">Street address</Label>
            <Input
              id="resident-address-line1"
              value={line1}
              onChange={(event) => setLine1(event.target.value)}
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="resident-address-city">City</Label>
              <Input
                id="resident-address-city"
                value={city}
                onChange={(event) => setCity(event.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="resident-address-state">State</Label>
              <Input
                id="resident-address-state"
                value={state}
                onChange={(event) => setState(event.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="resident-address-postal">Postal code</Label>
              <Input
                id="resident-address-postal"
                value={postalCode}
                onChange={(event) => setPostalCode(event.target.value)}
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              disabled={!line1.trim() || createMutation.isPending}
              onClick={() => createMutation.mutate()}
            >
              {createMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              Save address
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => setShowForm(false)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <Button type="button" size="sm" variant="outline" onClick={() => setShowForm(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Add address
        </Button>
      )}
    </div>
  );
}
